/**
 * Noibu Feature Flag: express checkout at the top of the mobile cart
 * (flag "2083-express-checkout-at-top-of-mobile-cart", Noibu A/B test 280).
 *
 * Structurally parallel to assets/noibu-flag-sticky-checkout.js. It is a
 * separate file on purpose: the other flag files define running experiments
 * and must not change mid-flight.
 *
 *   original                 -> html.nff-express--a  (control, the default)
 *   express-checkout-on-top  -> html.nff-express--b
 *
 * In variation B this script moves the theme's own dynamic checkout block
 * (.cart__dynamic-checkout-buttons, rendered by sections/main-cart-footer.liquid
 * under the Check out button) into a slot under the "Your cart" title row,
 * above the line items, with a small "Express checkout" label. Nothing is
 * duplicated: Shopify renders the wallet buttons once, and this only changes
 * where that block sits. The regular Check out button stays in the footer.
 * If the SDK later resolves to the control, the block is put back exactly
 * where it came from.
 *
 * main-cart-footer.liquid is NOT edited: it defines both arms of the running
 * 2083-cart-reassurance test (#48). sections/noibu-sticky-checkout-ab.liquid
 * (#134) is untouched too; its bar is position: fixed and does not interact
 * with the wallet block.
 *
 * Targeting (device type is mobile, phones and tablets) lives in the Noibu
 * test. There is deliberately no CSS breakpoint: gating by width would enrol
 * a landscape tablet into B while it still saw the control.
 *
 * Noibu's implementation code for test 280 (console > A/B tests >
 * Implementation), same template as every other test on this store:
 *
 *   function renderExpressCheckoutOnTop(container) {
 *     // TODO: the implementation for variation B
 *   }
 *
 *   function renderOriginal(container) {
 *     // TODO: the implementation for the default experience
 *   }
 *
 *   function render(container) {
 *     const { variation } = client.getStringDetails(
 *       "2083-express-checkout-at-top-of-mobile-cart",
 *       "original"
 *     );
 *
 *     switch (variation) {
 *       case "express-checkout-on-top":
 *         // Variation B
 *         return renderExpressCheckoutOnTop(container);
 *       case "original":
 *         // Variation A (control)
 *       default:
 *         return renderOriginal(container);
 *     }
 *   }
 *
 * SDK NOTE: the snippet destructures `variation`, but the details object the
 * SDK returns carries the arm as `variant` (and `value`). Copied verbatim,
 * every visitor would fall through to the control, so readVariation() reads
 * all three.
 */
(function () {
  'use strict';

  var FLAG = '2083-express-checkout-at-top-of-mobile-cart';
  var CONTROL = 'original';
  var VARIATION_B = 'express-checkout-on-top';
  var CACHE_KEY = 'nff.express-top';

  var REPORT_KEY = 'nff_express-top';
  var STATE_ATTR = 'data-nff-express-state';
  var ARM_ATTR = 'data-nff-express';

  var SDK_TIMEOUT_MS = 10000;

  var CLASSES = {};
  CLASSES[CONTROL] = 'nff-express--a';
  CLASSES[VARIATION_B] = 'nff-express--b';

  var root = document.documentElement;

  /* ------------------------------------------------------------------ */
  /* Variation B behaviour                                               */
  /* ------------------------------------------------------------------ */

  var home = null; // { parent, next } where the block lived in the footer

  function block() {
    return document.querySelector('.cart__dynamic-checkout-buttons');
  }

  function slot() {
    var existing = document.querySelector('[data-nff-express-slot]');
    if (existing) return existing;

    var template = document.querySelector('template[data-nff-express-slot-template]');
    var items = document.querySelector('cart-items');
    if (!template || !items) return null;

    var titleRow = items.querySelector('.title-wrapper-with-link');
    var node = template.content.firstElementChild.cloneNode(true);
    if (titleRow && titleRow.parentNode) {
      titleRow.parentNode.insertBefore(node, titleRow.nextSibling);
    } else {
      items.insertBefore(node, items.firstChild);
    }
    return node;
  }

  function renderExpressCheckoutOnTop() {
    var el = block();
    var target = slot();
    if (!el || !target) return false; // no wallets on this cart: nothing to move
    var mount = target.querySelector('[data-nff-express-mount]') || target;
    if (mount.contains(el)) return true;
    if (!home) home = { parent: el.parentNode, next: el.nextSibling };
    mount.appendChild(el);
    root.classList.add('nff-express--moved');
    return true;
  }

  function renderOriginal() {
    var el = block();
    if (home && el && home.parent && !home.parent.contains(el)) {
      home.parent.insertBefore(el, home.next && home.next.parentNode === home.parent ? home.next : null);
    }
    root.classList.remove('nff-express--moved');
  }

  function whenDom(callback) {
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', callback);
    else callback();
  }

  /* ------------------------------------------------------------------ */
  /* Flag plumbing (same shape as the other noibu-flag-*.js files)       */
  /* ------------------------------------------------------------------ */

  function reportState(state, detail) {
    root.setAttribute(STATE_ATTR, state);
    if (state === 'unavailable') {
      console.warn('[nff] ' + FLAG + ' flag unavailable, rendering control: ' + detail);
    }
    var tries = 0;
    (function attempt() {
      try {
        if (window.NOIBUJS && typeof window.NOIBUJS.addCustomAttribute === 'function') {
          window.NOIBUJS.addCustomAttribute(REPORT_KEY, state);
          return;
        }
      } catch (error) {
        // collect-core may still be booting. Retry rather than drop the exposure.
      }
      if (++tries < 40) setTimeout(attempt, 500);
    })();
  }

  function afterLoad(callback) {
    if (document.readyState === 'complete') {
      callback();
      return;
    }
    var fired = false;
    window.addEventListener('load', function () {
      if (fired) return;
      fired = true;
      callback();
    });
  }

  function whenClientReady(onReady, onUnavailable) {
    var done = false;
    var timer = null;

    function finish(handler, argument) {
      if (done) return;
      done = true;
      if (timer !== null) clearTimeout(timer);
      handler(argument);
    }

    afterLoad(function () {
      if (done) return;
      timer = setTimeout(function () {
        finish(
          onUnavailable,
          'no client ' + SDK_TIMEOUT_MS + 'ms after window.load. Is ' +
            'NOIBUJS_CONFIG.feature_flag_key provisioned for this store?'
        );
      }, SDK_TIMEOUT_MS);
    });

    window.addEventListener('noibuFeatureFlagError', function (event) {
      var message = (event && event.detail && event.detail.message) || 'unknown error';
      finish(onUnavailable, message);
    });

    if (window.NoibuFeatureFlag) {
      try {
        var client = window.NoibuFeatureFlag.getClient();
        client.addHandler('PROVIDER_READY', function () {
          finish(onReady, client);
        });
      } catch (error) {
        finish(onUnavailable, 'getClient() threw: ' + error);
      }
      return;
    }

    window.addEventListener('noibuFeatureFlagReady', function (event) {
      var ready = event && event.detail && event.detail.client;
      if (ready) finish(onReady, ready);
      else finish(onUnavailable, 'ready event carried no client');
    });
  }

  function activate(variation) {
    var className = CLASSES[variation] || CLASSES[CONTROL];
    if (!root.classList.contains(className)) {
      root.classList.remove(CLASSES[CONTROL], CLASSES[VARIATION_B]);
      root.classList.add(className);
      root.setAttribute(ARM_ATTR, variation);
      document.dispatchEvent(
        new CustomEvent('noibu:express-top-variation', { detail: { variation: variation } })
      );
    }
    whenDom(function () {
      if (variation === VARIATION_B) renderExpressCheckoutOnTop();
      else renderOriginal();
    });
  }

  function remember(variation) {
    try {
      window.localStorage.setItem(CACHE_KEY, variation);
    } catch (error) {
      /* no-op: private mode or an embedded webview */
    }
  }

  function forcedVariation() {
    // QA override: ?nff_express=express-checkout-on-top (or =original)
    try {
      var value = new URLSearchParams(window.location.search).get('nff_express');
      if (value === CONTROL || value === VARIATION_B) return value;
    } catch (error) {
      /* URLSearchParams unavailable: fall through to the flag. */
    }
    return null;
  }

  function readVariation(client) {
    if (typeof client.getStringDetails === 'function') {
      var details = client.getStringDetails(FLAG, CONTROL) || {};
      return details.variant || details.variation || details.value || CONTROL;
    }
    return client.getStringValue(FLAG, CONTROL);
  }

  function render(client) {
    var variation = readVariation(client);
    switch (variation) {
      case VARIATION_B:
        activate(VARIATION_B);
        remember(VARIATION_B);
        return VARIATION_B;
      case CONTROL:
      // falls through
      default:
        activate(CONTROL);
        remember(CONTROL);
        return CONTROL;
    }
  }

  // The paint-time hint may already have applied a cached arm; act on it now.
  if (root.getAttribute(ARM_ATTR) === VARIATION_B) whenDom(renderExpressCheckoutOnTop);

  var forced = forcedVariation();
  if (forced) {
    activate(forced);
    root.setAttribute(STATE_ATTR, 'forced');
    return;
  }

  whenClientReady(
    function (client) {
      reportState(render(client));
      client.addHandler('PROVIDER_CONFIGURATION_CHANGED', function () {
        render(client);
      });
    },
    function (detail) {
      if (!root.hasAttribute(ARM_ATTR)) activate(CONTROL);
      reportState('unavailable', detail);
    }
  );
})();
